import React, { useState, useEffect } from 'react';
import {
    Typography,
    Card,
    CardContent,
    Box,
    Button,
    Stack,
    Alert,
    CircularProgress,
    Grid,
    Modal,
    ModalDialog,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    Select,
    Option,
    Chip,
    IconButton,
    List,
    ListItem,
    ListItemContent,
    ListItemDecorator,
    Tabs,
    TabList,
    Tab,
    TabPanel,
    AspectRatio,
    Badge,
    Checkbox,
    Divider,
    Dropdown,
    Menu,
    MenuButton,
    MenuItem
} from '@mui/joy';
import { Add, Delete, DragIndicator, Cancel } from '@mui/icons-material';
import MainLayout from '../../components/Layout/MainLayout';
import FolderTreeSelector from '../../components/FolderTreeSelector';
import api from '../../utils/api';
import { openAuthenticatedLayoutPreview } from '../../utils/preview';
import { signageDomain } from '../../app/util/domain';

const updateAndReorderPaneItems = (paneItems, itemId, changes) => {
    const items = [...paneItems];
    const currentIndex = items.findIndex(item => item.id === itemId);

    if (currentIndex === -1) {
        return { items, targetIndex: -1, updatedItem: null };
    }

    const [item] = items.splice(currentIndex, 1);
    const parsedPosition = parseInt(changes.position, 10);
    const parsedDuration = parseInt(changes.duration, 10);
    const requestedPosition = Number.isNaN(parsedPosition) ? currentIndex + 1 : parsedPosition;
    const targetPosition = Math.min(Math.max(requestedPosition, 1), paneItems.length);
    const duration = Number.isNaN(parsedDuration)
        ? item.duration
        : Math.min(Math.max(parsedDuration, 1), 60);
    const displayName = typeof changes.displayName === 'string'
        ? changes.displayName.trim()
        : item.displayName;
    const updatedItem = {
        ...item,
        duration,
        animation: changes.animation || item.animation || 'slide-left'
    };

    if (displayName) {
        updatedItem.displayName = displayName;
    } else {
        delete updatedItem.displayName;
    }

    items.splice(targetPosition - 1, 0, updatedItem);

    return {
        items,
        targetIndex: targetPosition - 1,
        updatedItem
    };
};

const LayoutsPage = () => {
    const [layouts, setLayouts] = useState([]);
    const [content, setContent] = useState([]);
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Create/Edit layout modal
    const [openLayoutModal, setOpenLayoutModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingLayoutId, setEditingLayoutId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [layoutForm, setLayoutForm] = useState({
        layoutType: 'split',
        name: 'My Signage Layout',
        description: 'Animated content with a bottom marquee',
        leftPaneContent: [],
        rightPaneContent: [],
        marqueeContent: 'Welcome to Digital Signage • Latest Updates • Important Announcements'
    });

    // Content management modal
    const [openContentModal, setOpenContentModal] = useState(false);
    const [currentPane, setCurrentPane] = useState('leftPane');
    const [contentInsertIndex, setContentInsertIndex] = useState(null);
    const [editingContent, setEditingContent] = useState(null);
    const [selectedContentIds, setSelectedContentIds] = useState([]);
    const [selectedFolderId, setSelectedFolderId] = useState(null);
    const [showFolderStructure, setShowFolderStructure] = useState(true);
    const [activeContentTab, setActiveContentTab] = useState('all');
    const [contentForm, setContentForm] = useState({
        content: '',
        duration: 5,
        animation: 'slide-left'
    });

    // Drag and drop state
    const [draggedItem, setDraggedItem] = useState(null);
    const [draggedPane, setDraggedPane] = useState(null);
    const [draggedIndex, setDraggedIndex] = useState(null);

    // Pane item preview state
    const [previewPane, setPreviewPane] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [previewForm, setPreviewForm] = useState({
        displayName: '',
        position: 1,
        duration: 5,
        animation: 'slide-left'
    });
    const previewItems = previewPane ? layoutForm[`${previewPane}Content`] : [];
    const previewItem = previewItems[previewIndex] || null;
    const getPaneLabel = (pane) => pane === 'leftPane'
        ? (layoutForm.layoutType === 'single' ? 'Main Pane' : 'Left Pane')
        : 'Right Pane';

    // Layout properties modal
    const [openPropertiesModal, setOpenPropertiesModal] = useState(false);
    const [currentPropertyPane, setCurrentPropertyPane] = useState('leftPane');
    const [layoutProperties, setLayoutProperties] = useState({
        leftPane: {
            backgroundColor: '#000000',
            textColor: '#000000',
            padding: 20,
            borderRadius: 8,
            borderWidth: 2,
            borderColor: '#dee2e6',
            fontSize: 16,
            fontWeight: 'normal',
            textAlign: 'center',
            position: 'relative'
        },
        rightPane: {
            backgroundColor: '#000000',
            textColor: '#000000',
            padding: 20,
            borderRadius: 8,
            borderWidth: 2,
            borderColor: '#dee2e6',
            fontSize: 16,
            fontWeight: 'normal',
            textAlign: 'center',
            position: 'relative'
        },
        marquee: {
            backgroundColor: '#000000',
            textColor: '#ffffff',
            fontSize: 36,
            fontWeight: 'bold',
            speed: 25,
            padding: 15,
            borderTop: '3px solid #000000',
            borderBottom: '3px solid #000000'
        }
    });

    // Fetch layouts and content
    const fetchData = async () => {
        try {
            setLoading(true);
            setError('');

            const [layoutsRes, contentRes, foldersRes] = await Promise.all([
                api.get('/api/layouts'),
                api.get('/api/content', {
                    params: {
                        page: 1,
                        limit: 10000, // Load all content without pagination limit
                        sort_by: 'created_at',
                        sort_order: 'DESC'
                    }
                }),
                api.get('/api/folders/flat')
            ]);

            console.log('All layouts from API:', layoutsRes.data?.layouts?.length || 0);

            if (layoutsRes.data?.success) {
                // Filter for animated three pane layouts
                const animatedLayouts = (layoutsRes.data.layouts || []).filter(layout => {
                    try {
                        // Handle both string and object layout_config
                        let config;
                        if (typeof layout.layout_config === 'string') {
                            config = JSON.parse(layout.layout_config);
                        } else {
                            config = layout.layout_config || {};
                        }

                        console.log(`Layout ${layout.id} (${layout.template_name}):`, {
                            template_type: config.template_type,
                            animations: config.animations,
                            zones: config.zones,
                            isAnimated: config.template_type === 'animated_three_pane' ||
                                (config.animations === true && config.zones && config.zones.includes('leftPane'))
                        });

                        // Check for animated three pane layouts
                        return config.template_type === 'animated_three_pane' ||
                            (config.animations === true && config.zones && config.zones.includes('leftPane'));
                    } catch (e) {
                        console.error('Error parsing layout config:', e);
                        return false;
                    }
                });
                console.log('Filtered animated layouts:', animatedLayouts.length);
                setLayouts(animatedLayouts);
            }

            if (contentRes.data?.success) {
                setContent(contentRes.data.content || []);
            }

            if (foldersRes.data?.success) {
                setFolders(foldersRes.data.folders || []);
            }

        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Failed to load data';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Open modal for creating new layout
    const openCreateModal = () => {
        setIsEditing(false);
        setEditingLayoutId(null);
        setLayoutForm({
            layoutType: 'split',
            name: 'My Signage Layout',
            description: 'Animated content with a bottom marquee',
            leftPaneContent: [],
            rightPaneContent: [],
            marqueeContent: 'Welcome to Digital Signage • Latest Updates • Important Announcements'
        });
        setOpenLayoutModal(true);
    };

    // Open modal for editing existing layout
    const openEditModal = async (layout) => {
        try {
            setIsEditing(true);
            setEditingLayoutId(layout.id);

            // Parse existing content
            let config = {};
            if (typeof layout.layout_config === 'string') {
                config = JSON.parse(layout.layout_config);
            } else {
                config = layout.layout_config || {};
            }

            setLayoutForm({
                layoutType: config.layout_variant === 'single_marquee' ||
                    (Array.isArray(config.zones) && !config.zones.includes('rightPane'))
                    ? 'single'
                    : 'split',
                name: layout.template_name,
                description: layout.description || '',
                leftPaneContent: config.content?.leftPane || [],
                rightPaneContent: config.content?.rightPane || [],
                marqueeContent: config.content?.marqueePane || 'Welcome to Digital Signage'
            });

            // Load layout properties if they exist
            if (config.layoutProperties) {
                setLayoutProperties(config.layoutProperties);
            }

            setOpenLayoutModal(true);
        } catch (error) {
            console.error('Error loading layout for edit:', error);
            setError('Failed to load layout data');
        }
    };

    // Open content management modal
    const openContentManagement = (pane, insertIndex = null) => {
        setCurrentPane(pane);
        setContentInsertIndex(insertIndex);
        setEditingContent(null);
        setSelectedContentIds([]);
        setActiveContentTab('all');
        setContentForm({
            content: '',
            duration: 5,
            animation: 'slide-left'
        });
        setOpenContentModal(true);
    };

    const closeContentManagement = () => {
        setOpenContentModal(false);
        setContentInsertIndex(null);
    };

    // Get content statistics for tabs
    const getContentStats = () => {
        const stats = {
            total: content.length,
            image: content.filter(c => (c.type_name || '').toLowerCase() === 'image').length,
            video: content.filter(c => (c.type_name || '').toLowerCase() === 'video').length,
            document: content.filter(c => ['pdf', 'document'].includes((c.type_name || '').toLowerCase())).length,
            web: content.filter(c => (c.type_name || '').toLowerCase() === 'web').length
        };
        return stats;
    };

    // Folder helper functions
    const getFolderPath = (folderId) => {
        if (!folderId) return 'Root';
        const folder = folders.find(f => f.id === folderId);
        if (!folder) return 'Unknown Folder';

        const path = buildPathFromFolder(folder);
        return path.map(f => f.folder_name).join(' / ');
    };

    const buildPathFromFolder = (folder) => {
        const path = [folder];
        let current = folder;

        while (current.parent_folder_id) {
            const parent = folders.find(f => f.id === current.parent_folder_id);
            if (parent) {
                path.unshift(parent);
                current = parent;
            } else {
                break;
            }
        }

        return path;
    };

    const getContentInFolder = (folderId) => {
        if (!folderId) return content.filter(c => !c.folder_id);
        return content.filter(c => c.folder_id === folderId);
    };

    const getAllContentInFolderRecursive = (folderId) => {
        const directContent = getContentInFolder(folderId);
        const subfolders = folders.filter(f => f.parent_folder_id === folderId);

        let allContent = [...directContent];

        for (const subfolder of subfolders) {
            allContent = allContent.concat(getAllContentInFolderRecursive(subfolder.id));
        }

        return allContent;
    };

    const selectEntireFolder = (folderId) => {
        const folderContent = getAllContentInFolderRecursive(folderId);
        const contentIds = folderContent.map(c => c.id);

        setSelectedContentIds(prev => {
            const newSelection = [...prev];
            contentIds.forEach(id => {
                if (!newSelection.includes(id)) {
                    newSelection.push(id);
                }
            });
            return newSelection;
        });
    };

    // Filter content by category
    const getFilteredContent = () => {
        let filteredContent = content;

        // Filter by folder if selected - include all content from subfolders
        if (selectedFolderId !== null) {
            filteredContent = getAllContentInFolderRecursive(selectedFolderId);
        }

        // Since we removed tabs, show all content types
        return filteredContent;
    };

    // Toggle content selection
    const toggleContentSelection = (contentId) => {
        setSelectedContentIds(prev => {
            if (prev.includes(contentId)) {
                return prev.filter(id => id !== contentId);
            } else {
                // When selecting individual files, clear folder selection
                setSelectedFolderId(null);
                return [...prev, contentId];
            }
        });
    };

    const buildMediaUrl = (relativePath, baseUrl = signageDomain) => {
        if (!relativePath) return null;
        if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
            return relativePath;
        }
        return `${baseUrl}/${relativePath.replace(/^\/+/, '')}`;
    };

    // Build thumbnail URL
    const buildThumbnailUrl = (item) => {
        if (item.thumbnail_path) {
            return buildMediaUrl(item.thumbnail_path);
        } else if ((item.type_name || '').toLowerCase() === 'image' && item.file_path) {
            return buildMediaUrl(item.file_path);
        }
        return null;
    };

    const buildOriginalImageUrl = (item, baseUrl = signageDomain) => {
        if ((item?.type_name || '').toLowerCase() !== 'image' || !item.file_path) return null;
        return buildMediaUrl(item.file_path, baseUrl);
    };

    // Get fallback thumbnail
    const getFallbackThumbnail = () => {
        return 'data:image/svg+xml;utf8,' + encodeURIComponent(
            `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="200">
                <rect width="100%" height="100%" fill="#f2f4f7"/>
                <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" 
                      font-family="Arial" font-size="16" fill="#98a2b3">Preview not available</text>
            </svg>`
        );
    };

    // Add multiple content items to pane
    const addMultipleContentItems = (pane) => {
        if (selectedContentIds.length === 0) {
            setError('Please select at least one content item');
            return;
        }

        const newItems = selectedContentIds.map((contentId, index) => ({
            id: Date.now() + index,
            content: `CONTENT_ID:${contentId}`,
            duration: parseInt(contentForm.duration),
            animation: contentForm.animation
        }));

        const paneKey = `${pane}Content`;
        const isInserting = Number.isInteger(contentInsertIndex);
        const targetIndex = isInserting
            ? Math.min(Math.max(contentInsertIndex, 0), layoutForm[paneKey].length)
            : layoutForm[paneKey].length;

        setLayoutForm(prev => ({
            ...prev,
            [paneKey]: [
                ...prev[paneKey].slice(0, targetIndex),
                ...newItems,
                ...prev[paneKey].slice(targetIndex)
            ]
        }));

        if (isInserting) {
            setPreviewIndex(targetIndex);
            setPreviewForm({
                displayName: getPaneItemName(newItems[0]),
                position: targetIndex + 1,
                duration: newItems[0].duration,
                animation: newItems[0].animation
            });
        }

        // Reset form
        setSelectedContentIds([]);
        setContentForm({
            content: '',
            duration: 5,
            animation: 'slide-left'
        });
        closeContentManagement();
    };

    // Add single content item (for custom text)
    const addSingleContentItem = (pane) => {
        if (!contentForm.content) {
            setError('Please enter content text');
            return;
        }

        const newItem = {
            id: Date.now(),
            content: contentForm.content,
            duration: parseInt(contentForm.duration),
            animation: contentForm.animation
        };

        const paneKey = `${pane}Content`;
        const isInserting = Number.isInteger(contentInsertIndex);
        const targetIndex = isInserting
            ? Math.min(Math.max(contentInsertIndex, 0), layoutForm[paneKey].length)
            : layoutForm[paneKey].length;

        setLayoutForm(prev => ({
            ...prev,
            [paneKey]: [
                ...prev[paneKey].slice(0, targetIndex),
                newItem,
                ...prev[paneKey].slice(targetIndex)
            ]
        }));

        if (isInserting) {
            setPreviewIndex(targetIndex);
            setPreviewForm({
                displayName: getPaneItemName(newItem),
                position: targetIndex + 1,
                duration: newItem.duration,
                animation: newItem.animation
            });
        }

        // Reset form
        setContentForm({
            content: '',
            duration: 5,
            animation: 'slide-left'
        });
        closeContentManagement();
    };

    // Edit content item
    const editContentItem = (pane, itemId) => {
        const items = layoutForm[`${pane}Content`];
        const item = items.find(item => item.id === itemId);
        if (item) {
            setEditingContent(itemId);
            setContentForm({
                content: item.content,
                duration: item.duration,
                animation: item.animation
            });
            setOpenContentModal(true);
        }
    };

    // Update content item
    const updateContentItem = (pane) => {
        setLayoutForm(prev => ({
            ...prev,
            [`${pane}Content`]: prev[`${pane}Content`].map(item =>
                item.id === editingContent
                    ? { ...item, content: contentForm.content, duration: parseInt(contentForm.duration), animation: contentForm.animation }
                    : item
            )
        }));

        setOpenContentModal(false);
        setEditingContent(null);
        setContentForm({
            content: '',
            duration: 5,
            animation: 'slide-left'
        });
    };

    // Delete content item
    const deleteContentItem = (pane, itemId) => {
        setLayoutForm(prev => ({
            ...prev,
            [`${pane}Content`]: prev[`${pane}Content`].filter(item => item.id !== itemId)
        }));
    };

    // Drag and drop functions
    const handleDragStart = (e, item, pane, index) => {
        setDraggedItem(item);
        setDraggedPane(pane);
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', e.target.outerHTML);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e, targetPane, targetIndex) => {
        e.preventDefault();

        if (!draggedItem || !draggedPane) return;

        // If dropping in the same pane, reorder
        if (draggedPane === targetPane) {
            setLayoutForm(prev => {
                const items = [...prev[`${targetPane}Content`]];
                const draggedItem = items.splice(draggedIndex, 1)[0];
                items.splice(targetIndex, 0, draggedItem);

                return {
                    ...prev,
                    [`${targetPane}Content`]: items
                };
            });
        } else {
            // If dropping in a different pane, move the item
            setLayoutForm(prev => {
                const sourceItems = [...prev[`${draggedPane}Content`]];
                const targetItems = [...prev[`${targetPane}Content`]];

                const draggedItem = sourceItems.splice(draggedIndex, 1)[0];
                targetItems.splice(targetIndex, 0, draggedItem);

                return {
                    ...prev,
                    [`${draggedPane}Content`]: sourceItems,
                    [`${targetPane}Content`]: targetItems
                };
            });
        }

        // Reset drag state
        setDraggedItem(null);
        setDraggedPane(null);
        setDraggedIndex(null);
    };

    // Layout properties functions
    const openPropertiesEditor = (pane) => {
        setCurrentPropertyPane(pane);
        setOpenPropertiesModal(true);
    };

    const updateLayoutProperty = (property, value) => {
        setLayoutProperties(prev => ({
            ...prev,
            [currentPropertyPane]: {
                ...prev[currentPropertyPane],
                [property]: value
            }
        }));
    };

    const resetLayoutProperties = (pane) => {
        const defaultProperties = {
            leftPane: {
                backgroundColor: '#f8f9fa',
                textColor: '#333333',
                padding: 20,
                borderRadius: 8,
                borderWidth: 2,
                borderColor: '#dee2e6',
                fontSize: 16,
                fontWeight: 'normal',
                textAlign: 'center',
                position: 'relative'
            },
            rightPane: {
                backgroundColor: '#fff3cd',
                textColor: '#333333',
                padding: 20,
                borderRadius: 8,
                borderWidth: 2,
                borderColor: '#ffeaa7',
                fontSize: 16,
                fontWeight: 'normal',
                textAlign: 'center',
                position: 'relative'
            },
            marquee: {
                backgroundColor: '#000000',
                textColor: '#ffffff',
                fontSize: 24,
                fontWeight: 'bold',
                speed: 25,
                padding: 15,
                borderTop: '3px solid #1976d2',
                borderBottom: '3px solid #1976d2'
            }
        };

        setLayoutProperties(prev => ({
            ...prev,
            [pane]: defaultProperties[pane]
        }));
    };

    // Save layout (create or update)
    const handleSaveLayout = async () => {
        try {
            setSaving(true);
            setError('');

            const isSinglePaneLayout = layoutForm.layoutType === 'single';
            const savedRightPaneContent = isSinglePaneLayout ? [] : layoutForm.rightPaneContent;
            const renderPaneItems = (items, emptyLabel) => items.length > 0
                ? items.map((item, index) =>
                    `<div class="content-item" style="display: ${index === 0 ? 'flex' : 'none'}; opacity: ${index === 0 ? '1' : '0'};">${item.content}</div>`
                ).join('')
                : `<div class="content-item" style="display: flex; opacity: 1;"><div class="text-content">🎬<br>${emptyLabel}<br><small>No content assigned</small></div></div>`;

            // Generate animated HTML template
            const htmlTemplate = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Digital Signage - ${layoutForm.name}</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
            font-family: 'Arial', sans-serif;
            background: #000;
            overflow: hidden;
            width: 100vw;
            height: 100vh;
            display: flex;
            flex-direction: column;
        }
        .main-content {
            flex: 1;
            display: flex;
            height: 90vh;
            gap: 2px;
        }
        .pane {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
            position: relative;
        }
         .left-pane { 
             background: ${layoutProperties.leftPane.backgroundColor}; 
             color: ${layoutProperties.leftPane.textColor};
             padding: ${layoutProperties.leftPane.padding}px;
             border-radius: ${layoutProperties.leftPane.borderRadius}px;
             border: ${layoutProperties.leftPane.borderWidth}px solid ${layoutProperties.leftPane.borderColor};
             font-size: ${layoutProperties.leftPane.fontSize}px;
             font-weight: ${layoutProperties.leftPane.fontWeight};
             text-align: ${layoutProperties.leftPane.textAlign};
         }
         .right-pane { 
             background: ${layoutProperties.rightPane.backgroundColor}; 
             color: ${layoutProperties.rightPane.textColor};
             padding: ${layoutProperties.rightPane.padding}px;
             border-radius: ${layoutProperties.rightPane.borderRadius}px;
             border: ${layoutProperties.rightPane.borderWidth}px solid ${layoutProperties.rightPane.borderColor};
             font-size: ${layoutProperties.rightPane.fontSize}px;
             font-weight: ${layoutProperties.rightPane.fontWeight};
             text-align: ${layoutProperties.rightPane.textAlign};
         }
        .content-display {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            position: relative;
        }
        .content-item {
            position: absolute;
            width: 99%;
            height: 99%;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 1;
            transition: opacity 0.5s ease-in-out, transform 0.5s ease-in-out;
        }
        .content-item.active {
            opacity: 1;
            z-index: 10;
        }
        .content-item img, .content-item video {
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
        }
        .text-content {
            font-size: 4vw;
            font-weight: 600;
            padding: 3vw;
            word-wrap: break-word;
            line-height: 1.2;
        }
        .fade-in { opacity: 0; animation: fadeIn 0.5s ease-in-out forwards; }
        .fade-out { opacity: 1; animation: fadeOut 0.5s ease-in-out forwards; }
        .slide-left-in { transform: translateX(-100%); animation: slideLeftIn 0.5s ease-in-out forwards; }
        .slide-left-out { transform: translateX(0); animation: slideLeftOut 0.5s ease-in-out forwards; }
        .slide-right-in { transform: translateX(100%); animation: slideRightIn 0.5s ease-in-out forwards; }
        .slide-right-out { transform: translateX(0); animation: slideRightOut 0.5s ease-in-out forwards; }
        .flip-left-in { transform: rotateY(90deg); animation: flipLeftIn 0.5s ease-in-out forwards; }
        .flip-left-out { transform: rotateY(0deg); animation: flipLeftOut 0.5s ease-in-out forwards; }
        .flip-right-in { transform: rotateY(-90deg); animation: flipRightIn 0.5s ease-in-out forwards; }
        .flip-right-out { transform: rotateY(0deg); animation: flipRightOut 0.5s ease-in-out forwards; }
        
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fadeOut { from { opacity: 1; } to { opacity: 0; } }
        @keyframes slideLeftIn { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        @keyframes slideLeftOut { from { transform: translateX(0); } to { transform: translateX(-100%); } }
        @keyframes slideRightIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes slideRightOut { from { transform: translateX(0); } to { transform: translateX(100%); } }
        @keyframes flipLeftIn { from { transform: rotateY(90deg); } to { transform: rotateY(0deg); } }
        @keyframes flipLeftOut { from { transform: rotateY(0deg); } to { transform: rotateY(90deg); } }
        @keyframes flipRightIn { from { transform: rotateY(-90deg); } to { transform: rotateY(0deg); } }
        @keyframes flipRightOut { from { transform: rotateY(0deg); } to { transform: rotateY(-90deg); } }
        
         .marquee-container {
             height: 10vh;
             background: ${layoutProperties.marquee.backgroundColor};
             color: ${layoutProperties.marquee.textColor};
             display: flex;
             align-items: center;
             overflow: hidden;
             position: relative;
             padding: ${layoutProperties.marquee.padding}px;
             border-top: ${layoutProperties.marquee.borderTop};
             border-bottom: ${layoutProperties.marquee.borderBottom};
         }
        .marquee-label {
            position: absolute;
            left: 3vw;
            background: rgba(255,255,255,0.2);
            padding: 1vw 2vw;
            border-radius: 1vw;
            font-size: 2.5vw;
            font-weight: 700;
            z-index: 10;
        }
         .marquee-content {
             white-space: nowrap;
             font-size: ${layoutProperties.marquee.fontSize}px;
             font-weight: ${layoutProperties.marquee.fontWeight};
             animation: scroll-text ${layoutProperties.marquee.speed}s linear infinite;
             padding-left: 100%;
         }
        @keyframes scroll-text {
            0% { transform: translateX(0); }
            100% { transform: translateX(-100%); }
        }
    </style>
</head>
<body>
    <div class="main-content">
        <div class="pane left-pane">
            <div class="content-display" id="leftPane">
                ${renderPaneItems(layoutForm.leftPaneContent, isSinglePaneLayout ? 'Main Pane' : 'Left Pane')}
            </div>
        </div>
        ${isSinglePaneLayout ? '' : `<div class="pane right-pane">
            <div class="content-display" id="rightPane">
                ${renderPaneItems(savedRightPaneContent, 'Right Pane')}
            </div>
        </div>`}
    </div>
    <div class="marquee-container">
        <div class="marquee-label">LIVE</div>
         <div class="marquee-content">
             ${layoutForm.marqueeContent.split('\n').filter(line => line.trim()).join('&nbsp;'.repeat(150))}
         </div>
    </div>

    <script>
        // Animation data
        window.leftPaneData = ${JSON.stringify(layoutForm.leftPaneContent.map(item => ({
                    content: item.content,
                    duration: item.duration,
                    animation: item.animation
                })))};
        
        window.rightPaneData = ${JSON.stringify(savedRightPaneContent.map(item => ({
                    content: item.content,
                    duration: item.duration,
                    animation: item.animation
                })))};

        // Animation Controller (same as template)
        class PaneAnimationController {
            constructor(paneId) {
                this.paneId = paneId;
                this.pane = document.getElementById(paneId);
                this.currentIndex = 0;
                this.items = [];
                this.timeouts = [];
            }

            setContentItems(items) {
                console.log('setContentItems called with', items.length, 'items');
                this.items = items;
                this.currentIndex = 0;
                this.clearTimeouts();
                this.renderItems();
                if (items.length > 1) {
                    console.log('Starting rotation for', items.length, 'items');
                    this.startRotation();
                }
            }

            renderItems() {
                if (!this.pane || this.items.length === 0) return;
                
                // Check if content is already rendered correctly
                const existingItems = this.pane.querySelectorAll('.content-item');
                if (existingItems.length === this.items.length) {
                    console.log('Content already rendered correctly, skipping renderItems()');
                    // Just ensure proper display states
                    existingItems.forEach((item, index) => {
                        item.style.display = index === 0 ? 'flex' : 'none';
                        item.style.opacity = index === 0 ? '1' : '0';
                    });
                    return;
                }
                
                // Only clear and recreate if content doesn't match
                console.log('Recreating content items...');
                this.pane.innerHTML = '';
                
                this.items.forEach((item, index) => {
                    const contentDiv = document.createElement('div');
                    contentDiv.className = 'content-item';
                    contentDiv.innerHTML = item.content;
                    contentDiv.style.display = index === 0 ? 'flex' : 'none';
                    contentDiv.style.opacity = index === 0 ? '1' : '0';
                    this.pane.appendChild(contentDiv);
                });
            }

            startRotation() {
                if (this.items.length <= 1) return;

                const rotateToNext = () => {
                    const currentItem = this.items[this.currentIndex];
                    const nextIndex = (this.currentIndex + 1) % this.items.length;
                    const nextItem = this.items[nextIndex];

                    const currentElement = this.pane.children[this.currentIndex];
                    if (currentElement) {
                        // Start fade out animation
                        currentElement.style.opacity = '0';
                        currentElement.classList.add(this.getOutAnimation(currentItem.animation));
                        
                        setTimeout(() => {
                            currentElement.style.display = 'none';
                            
                            const nextElement = this.pane.children[nextIndex];
                            if (nextElement) {
                                nextElement.style.display = 'flex';
                                nextElement.style.opacity = '1';
                                nextElement.classList.add(this.getInAnimation(nextItem.animation));
                                
                                setTimeout(() => {
                                    currentElement.classList.remove(this.getOutAnimation(currentItem.animation));
                                    nextElement.classList.remove(this.getInAnimation(nextItem.animation));
                                }, 500);
                            }
                        }, 500);
                    }

                    this.currentIndex = nextIndex;
                    
                    const timeout = setTimeout(rotateToNext, (nextItem.duration || 5) * 1000);
                    this.timeouts.push(timeout);
                };

                const initialTimeout = setTimeout(rotateToNext, (this.items[0].duration || 5) * 1000);
                this.timeouts.push(initialTimeout);
            }

            getInAnimation(animation) {
                switch (animation) {
                    case 'slide-left': return 'slide-left-in';
                    case 'slide-right': return 'slide-right-in';
                    case 'flip-left': return 'flip-left-in';
                    case 'flip-right': return 'flip-right-in';
                    case 'fade': 
                    default: return 'fade-in';
                }
            }

            getOutAnimation(animation) {
                switch (animation) {
                    case 'slide-left': return 'slide-left-out';
                    case 'slide-right': return 'slide-right-out';
                    case 'flip-left': return 'flip-left-out';
                    case 'flip-right': return 'flip-right-out';
                    case 'fade':
                    default: return 'fade-out';
                }
            }

            clearTimeouts() {
                this.timeouts.forEach(timeout => clearTimeout(timeout));
                this.timeouts = [];
            }

            destroy() {
                this.clearTimeouts();
            }
        }

        let leftController, rightController;

        function initializeAnimations() {
            console.log('Initializing animations...');
            
            const leftPaneData = window.leftPaneData || [];
            const leftPane = document.getElementById('leftPane');
            if (leftPaneData.length > 0 && leftPane) {
                const existingItems = leftPane.querySelectorAll('.content-item');
                console.log('Left pane - Data items:', leftPaneData.length, 'Existing HTML items:', existingItems.length);
                
                if (existingItems.length === leftPaneData.length) {
                    // Content already exists, just initialize controller without re-rendering
                    leftController = new PaneAnimationController('leftPane');
                    leftController.items = leftPaneData;
                    leftController.currentIndex = 0;
                    if (leftPaneData.length > 1) {
                        console.log('Starting left pane rotation with existing content');
                        leftController.startRotation();
                    }
                } else {
                    // Need to render content
                    leftController = new PaneAnimationController('leftPane');
                    leftController.setContentItems(leftPaneData);
                }
            }

            const rightPaneData = window.rightPaneData || [];
            const rightPane = document.getElementById('rightPane');
            if (rightPaneData.length > 0 && rightPane) {
                const existingItems = rightPane.querySelectorAll('.content-item');
                console.log('Right pane - Data items:', rightPaneData.length, 'Existing HTML items:', existingItems.length);
                
                if (existingItems.length === rightPaneData.length) {
                    // Content already exists, just initialize controller without re-rendering
                    rightController = new PaneAnimationController('rightPane');
                    rightController.items = rightPaneData;
                    rightController.currentIndex = 0;
                    if (rightPaneData.length > 1) {
                        console.log('Starting right pane rotation with existing content');
                        rightController.startRotation();
                    }
                } else {
                    // Need to render content
                    rightController = new PaneAnimationController('rightPane');
                    rightController.setContentItems(rightPaneData);
                }
            }
        }

        document.addEventListener('DOMContentLoaded', initializeAnimations);

        window.addEventListener('beforeunload', () => {
            if (leftController) leftController.destroy();
            if (rightController) rightController.destroy();
        });
    </script>
</body>
</html>`;


            // Create layout configuration
            const layoutConfig = {
                template_type: 'animated_three_pane',
                layout_variant: isSinglePaneLayout ? 'single_marquee' : 'split_marquee',
                zones: isSinglePaneLayout
                    ? ['leftPane', 'marqueePane']
                    : ['leftPane', 'rightPane', 'marqueePane'],
                html_content: htmlTemplate,
                content: {
                    leftPane: layoutForm.leftPaneContent,
                    rightPane: savedRightPaneContent,
                    marqueePane: layoutForm.marqueeContent
                },
                layoutProperties: layoutProperties,
                animations: true,
                responsive: true,
                tv_compatible: true
            };

            let data;
            if (isEditing && editingLayoutId) {
                // Update existing layout
                const response = await api.put(`/api/layouts/${editingLayoutId}`, {
                    template_name: layoutForm.name,
                    description: layoutForm.description,
                    layout_config: layoutConfig
                });
                data = response.data;
            } else {
                // Create new layout
                const layoutData = {
                    template_name: layoutForm.name,
                    description: layoutForm.description,
                    grid_columns: isSinglePaneLayout ? 1 : 3,
                    grid_rows: 2,
                    layout_config: layoutConfig
                };
                const response = await api.post('/api/layouts', layoutData);
                data = response.data;
            }

            if (data?.success) {
                setOpenLayoutModal(false);
                setLayoutForm({
                    layoutType: 'split',
                    name: 'My Signage Layout',
                    description: 'Animated content with a bottom marquee',
                    leftPaneContent: [],
                    rightPaneContent: [],
                    marqueeContent: 'Welcome to Digital Signage • Latest Updates • Important Announcements'
                });
                setIsEditing(false);
                setEditingLayoutId(null);
                fetchData(); // Refresh layouts list
            } else {
                setError(data?.message || `Failed to ${isEditing ? 'update' : 'create'} layout`);
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || `Failed to ${isEditing ? 'update' : 'create'} layout`;
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    // Generate content options for dropdowns
    const getContentOptions = () => {
        return content.map(item => {
            // Match ContentPage logic for content type detection
            const contentType = item.type_name || item.content_type;
            let icon = '📝';

            if (contentType === 'Image' || contentType === 'image') {
                icon = '🖼️';
            } else if (contentType === 'Video' || contentType === 'video') {
                icon = '🎥';
            } else if (contentType === 'PDF' || contentType === 'pdf' || contentType === 'document') {
                icon = '📄';
            } else if (contentType === 'Web' || contentType === 'html' || contentType === 'web') {
                icon = '🌐';
            }

            return {
                value: `CONTENT_ID:${item.id}`, // Store content ID for backend resolution
                label: `${icon} ${item.content_name || item.title}`,
                contentType: contentType,
                filePath: item.file_path
            };
        });
    };

    // Get content name by ID
    const getContentNameById = (contentId) => {
        const contentIdNumber = contentId.replace('CONTENT_ID:', '');
        const contentItem = content.find(item => item.id.toString() === contentIdNumber);
        return contentItem ? (contentItem.content_name || contentItem.title || `Content ${contentIdNumber}`) : `Unknown Content ${contentIdNumber}`;
    };

    const getPaneItemName = (item) => {
        if (!item) return '';
        if (item.displayName?.trim()) return item.displayName.trim();
        if (item.content && item.content.includes('CONTENT_ID:')) {
            return getContentNameById(item.content);
        }
        return item.content || 'Content item';
    };

    // Get content item by ID
    const getContentItemById = (contentId) => {
        if (!contentId || !contentId.includes('CONTENT_ID:')) return null;
        const contentIdNumber = contentId.replace('CONTENT_ID:', '');
        return content.find(item => item.id.toString() === contentIdNumber) || null;
    };

    // Get thumbnail for content item
    const getContentThumbnail = (item) => {
        if (item.content && item.content.includes('CONTENT_ID:')) {
            const contentItem = getContentItemById(item.content);
            if (contentItem) {
                return buildThumbnailUrl(contentItem) || getFallbackThumbnail();
            }
        }
        return null;
    };

    const getContentPreviewUrl = (item) => {
        if (!item?.content?.includes('CONTENT_ID:')) return null;
        return buildOriginalImageUrl(getContentItemById(item.content));
    };

    const getContentPreviewFallbackUrl = (item) => {
        if (!item?.content?.includes('CONTENT_ID:')) return null;
        const contentItem = getContentItemById(item.content);
        const primaryUrl = buildOriginalImageUrl(contentItem);
        const productionUrl = buildOriginalImageUrl(contentItem, 'https://tv.vectorindia.org');
        return productionUrl && productionUrl !== primaryUrl ? productionUrl : null;
    };

    const openItemPreview = (pane, index) => {
        const items = layoutForm[`${pane}Content`];
        const item = items[index];

        if (!item) return;

        setPreviewPane(pane);
        setPreviewIndex(index);
        setPreviewForm({
            displayName: getPaneItemName(item),
            position: index + 1,
            duration: item.duration,
            animation: item.animation
        });
    };

    const closeItemPreview = () => {
        setPreviewPane(null);
        setPreviewIndex(0);
        setPreviewForm({ displayName: '', position: 1, duration: 5, animation: 'slide-left' });
    };

    const navigateItemPreview = (direction) => {
        const nextIndex = previewIndex + direction;

        if (nextIndex < 0 || nextIndex >= previewItems.length) return;

        const item = previewItems[nextIndex];
        setPreviewIndex(nextIndex);
        setPreviewForm({
            displayName: getPaneItemName(item),
            position: nextIndex + 1,
            duration: item.duration,
            animation: item.animation
        });
    };

    const savePreviewChanges = () => {
        if (!previewPane || !previewItem) return;

        const paneKey = `${previewPane}Content`;
        const result = updateAndReorderPaneItems(previewItems, previewItem.id, previewForm);

        if (result.targetIndex === -1) return;

        setLayoutForm(prev => ({
            ...prev,
            [paneKey]: result.items
        }));
        setPreviewIndex(result.targetIndex);
        setPreviewForm({
            displayName: getPaneItemName(result.updatedItem),
            position: result.targetIndex + 1,
            duration: result.updatedItem.duration,
            animation: result.updatedItem.animation
        });
    };

    const deletePreviewItem = () => {
        if (!previewPane || !previewItem) return;

        const itemName = getPaneItemName(previewItem);
        if (!window.confirm(`Remove "${itemName}" from this layout?`)) return;

        const paneKey = `${previewPane}Content`;
        const remainingItems = previewItems.filter(item => item.id !== previewItem.id);

        setLayoutForm(prev => ({
            ...prev,
            [paneKey]: remainingItems
        }));

        if (remainingItems.length === 0) {
            closeItemPreview();
            return;
        }

        const nextIndex = Math.min(previewIndex, remainingItems.length - 1);
        const nextItem = remainingItems[nextIndex];

        setPreviewIndex(nextIndex);
        setPreviewForm({
            displayName: getPaneItemName(nextItem),
            position: nextIndex + 1,
            duration: nextItem.duration,
            animation: nextItem.animation
        });
    };

    // Delete layout
    const handleDeleteLayout = async (layoutId) => {
        if (!window.confirm('Are you sure you want to delete this layout?')) return;

        try {
            const { data } = await api.delete(`/api/layouts/${layoutId}`);
            if (data?.success) {
                fetchData(); // Refresh list
            } else {
                alert(data?.message || 'Failed to delete layout');
            }
        } catch (err) {
            alert(err?.response?.data?.message || 'Failed to delete layout');
        }
    };

    return (
        <MainLayout currentPage="layouts">
            <Box>
                <Typography level="h2" sx={{ fontWeight: 600, mb: 1 }}>
                    🎬 Layout Management
                </Typography>
                <Typography level="body-md" sx={{ color: 'neutral.600', mb: 3 }}>
                    Create TV-ready split-screen or single-pane layouts with a bottom marquee.
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                    <Button
                        variant="solid"
                        color="success"
                        onClick={openCreateModal}
                    >
                        🎬 Create Layout
                    </Button>
                    <Button variant="outlined" onClick={() => fetchData()}>
                        🔄 Refresh
                    </Button>
                    <Box sx={{ ml: 'auto', color: 'neutral.600' }}>
                        {layouts.length} layouts
                    </Box>
                </Stack>

                {error && (
                    <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size="sm" />
                    </Box>
                ) : (
                    <>
                        {layouts.length === 0 ? (
                            <Card variant="outlined" sx={{ textAlign: 'center', py: 6 }}>
                                <CardContent>
                                    <Typography level="h4" sx={{ mb: 2 }}>
                                        🎬 No Layouts Yet
                                    </Typography>
                                    <Typography level="body-md" sx={{ color: 'neutral.600', mb: 3 }}>
                                        Create your first three-pane layout with multiple content items and smooth transitions.
                                    </Typography>
                                    <Button
                                        variant="solid"
                                        color="success"
                                        onClick={openCreateModal}
                                    >
                                        🎬 Create Your First Layout
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            <Grid container spacing={3}>
                                {layouts.map((layout) => (
                                    <Grid xs={12} md={6} lg={4} key={layout.id}>
                                        <Card variant="outlined" sx={{ height: '100%' }}>
                                            <CardContent>
                                                <Stack spacing={2}>
                                                    <Box>
                                                        <Typography level="h4" sx={{ fontWeight: 600, mb: 1 }}>
                                                            🎬 {layout.template_name}
                                                        </Typography>
                                                        <Typography level="body-sm" sx={{ color: 'neutral.600', mb: 2 }}>
                                                            {layout.description}
                                                        </Typography>
                                                        <Stack direction="row" spacing={0.5}>
                                                            <Chip size="sm" variant="soft" color="primary">🎬 Animated</Chip>
                                                            <Chip size="sm" variant="soft" color="success">📺 TV Ready</Chip>
                                                        </Stack>
                                                    </Box>

                                                    <Box sx={{
                                                        height: '120px',
                                                        background: 'linear-gradient(135deg, #7b1fa2, #9c27b0)',
                                                        color: 'white',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        borderRadius: 'sm',
                                                        fontSize: '0.875rem',
                                                        fontWeight: 600,
                                                        flexDirection: 'column'
                                                    }}>
                                                        🎬<br />
                                                        Animated Layout<br />
                                                        <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                                            Multi-Content • Animations
                                                        </Typography>
                                                    </Box>

                                                    <Stack direction="row" spacing={0.5}>
                                                        <Button
                                                            size="sm"
                                                            variant="outlined"
                                                            onClick={() => openAuthenticatedLayoutPreview(layout.id)}
                                                        >
                                                            👁️ Preview
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outlined"
                                                            color="primary"
                                                            onClick={() => openEditModal(layout)}
                                                        >
                                                            ✏️ Edit
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outlined"
                                                            color="success"
                                                            onClick={() => window.open('/device-groups', '_blank')}
                                                        >
                                                            📡 Assign to Groups
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outlined"
                                                            color="danger"
                                                            onClick={() => handleDeleteLayout(layout.id)}
                                                        >
                                                            🗑️ Delete
                                                        </Button>
                                                    </Stack>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </>
                )}

                {/* Create/Edit Layout Modal */}
                <Modal open={openLayoutModal} onClose={() => setOpenLayoutModal(false)}>
                    <ModalDialog sx={{ width: '90vw', maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto' }}>
                        <Typography level="h4" sx={{ mb: 2 }}>
                            {isEditing ? '✏️ Edit Layout' : '🎬 Create Layout'}
                        </Typography>

                        <Stack spacing={3}>
                            <Alert color="primary" variant="soft">
                                {isEditing ? 'Edit your TV-ready layout, content rotation, animations, and bottom marquee.' : 'Choose a split-screen or single-pane TV layout, then add content and a bottom marquee.'}
                                <br />
                                <Typography level="body-xs" sx={{ mt: 1, opacity: 0.8 }}>
                                    💡 <strong>Tips:</strong> Drag items to reorder them{layoutForm.layoutType === 'split' ? ' or move them between panes' : ''}. Click a row to preview, navigate, and edit its position, duration, animation, or name.
                                </Typography>
                            </Alert>

                            <Grid container spacing={2}>
                                <Grid xs={12} md={4}>
                                    <FormControl>
                                        <FormLabel>Layout Format</FormLabel>
                                        <Select
                                            value={layoutForm.layoutType}
                                            onChange={(e, value) => setLayoutForm(prev => ({ ...prev, layoutType: value }))}
                                        >
                                            <Option value="split">Two panes + bottom marquee</Option>
                                            <Option value="single">Single pane + bottom marquee</Option>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid xs={12} md={4}>
                                    <FormControl required>
                                        <FormLabel>Layout Name</FormLabel>
                                        <Input
                                            value={layoutForm.name}
                                            onChange={(e) => setLayoutForm(prev => ({ ...prev, name: e.target.value }))}
                                            placeholder="My Signage Layout"
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid xs={12} md={4}>
                                    <FormControl>
                                        <FormLabel>Description</FormLabel>
                                        <Input
                                            value={layoutForm.description}
                                            onChange={(e) => setLayoutForm(prev => ({ ...prev, description: e.target.value }))}
                                            placeholder="Layout description"
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid xs={12} md={layoutForm.layoutType === 'single' ? 12 : 6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                <Typography level="h4" sx={{ fontWeight: 600 }}>
                                                    {layoutForm.layoutType === 'single' ? '🎬 Main Pane Content' : '📊 Left Pane Content'}
                                                </Typography>
                                                <Button
                                                    size="sm"
                                                    variant="outlined"
                                                    onClick={() => openPropertiesEditor('leftPane')}
                                                >
                                                    ⚙️ Properties
                                                </Button>
                                            </Box>

                                            <Stack spacing={2}>
                                                <List>
                                                    {layoutForm.leftPaneContent.map((item, index) => (
                                                        <ListItem
                                                            key={item.id}
                                                            draggable
                                                            onDragStart={(e) => handleDragStart(e, item, 'leftPane', index)}
                                                            onDragOver={handleDragOver}
                                                            onDrop={(e) => handleDrop(e, 'leftPane', index)}
                                                            onClick={() => openItemPreview('leftPane', index)}
                                                            sx={{
                                                                cursor: 'pointer',
                                                                border: '1px solid',
                                                                borderColor: 'neutral.200',
                                                                borderRadius: 'sm',
                                                                mb: 1,
                                                                p: 2,
                                                                backgroundColor: 'background.body',
                                                                '&:hover': {
                                                                    backgroundColor: 'neutral.50'
                                                                }
                                                            }}
                                                        >
                                                            <ListItemDecorator sx={{ mr: 2, cursor: 'grab' }} onClick={(e) => e.stopPropagation()}>
                                                                <DragIndicator sx={{ color: 'neutral.500' }} />
                                                            </ListItemDecorator>
                                                            {getContentThumbnail(item) && (
                                                                <ListItemDecorator sx={{ mr: 2 }}>
                                                                    <Box
                                                                        component="img"
                                                                        src={getContentThumbnail(item)}
                                                                        alt={getPaneItemName(item)}
                                                                        sx={{
                                                                            width: 100,
                                                                            height: 100,
                                                                            objectFit: 'cover',
                                                                            borderRadius: '4px',
                                                                            border: '1px solid',
                                                                            borderColor: 'neutral.200'
                                                                        }}
                                                                    />
                                                                </ListItemDecorator>
                                                            )}
                                                            <ListItemContent>
                                                                <Typography level="body-sm" sx={{ fontWeight: 600, mb: 1 }}>
                                                                    {index + 1}. {getPaneItemName(item)}
                                                                </Typography>

                                                                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                                                    <Chip size="sm" color="primary" variant="soft">
                                                                        {item.duration}s
                                                                    </Chip>
                                                                    <Chip size="sm" color="neutral" variant="soft">
                                                                        {item.animation.replace('-', ' ')}
                                                                    </Chip>
                                                                </Stack>
                                                            </ListItemContent>
                                                            <ListItemDecorator>
                                                                <IconButton
                                                                    size="sm"
                                                                    variant="outlined"
                                                                    color="danger"
                                                                    aria-label={`Delete ${getPaneItemName(item)}`}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        deleteContentItem('leftPane', item.id);
                                                                    }}
                                                                >
                                                                    <Delete />
                                                                </IconButton>
                                                            </ListItemDecorator>
                                                        </ListItem>
                                                    ))}
                                                </List>

                                                <Button
                                                    variant="outlined"
                                                    startDecorator={<Add />}
                                                    onClick={() => openContentManagement('leftPane')}
                                                >
                                                    Add Content Item
                                                </Button>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {layoutForm.layoutType === 'split' && <Grid xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                <Typography level="h4" sx={{ fontWeight: 600 }}>
                                                    📈 Right Pane Content
                                                </Typography>
                                                <Button
                                                    size="sm"
                                                    variant="outlined"
                                                    onClick={() => openPropertiesEditor('rightPane')}
                                                >
                                                    ⚙️ Properties
                                                </Button>
                                            </Box>

                                            <Stack spacing={2}>
                                                <List>
                                                    {layoutForm.rightPaneContent.map((item, index) => (
                                                        <ListItem
                                                            key={item.id}
                                                            draggable
                                                            onDragStart={(e) => handleDragStart(e, item, 'rightPane', index)}
                                                            onDragOver={handleDragOver}
                                                            onDrop={(e) => handleDrop(e, 'rightPane', index)}
                                                            onClick={() => openItemPreview('rightPane', index)}
                                                            sx={{
                                                                cursor: 'pointer',
                                                                border: '1px solid',
                                                                borderColor: 'neutral.200',
                                                                borderRadius: 'sm',
                                                                mb: 1,
                                                                p: 2,
                                                                backgroundColor: 'background.body',
                                                                '&:hover': {
                                                                    backgroundColor: 'neutral.50'
                                                                }
                                                            }}
                                                        >
                                                            <ListItemDecorator sx={{ mr: 2, cursor: 'grab' }} onClick={(e) => e.stopPropagation()}>
                                                                <DragIndicator sx={{ color: 'neutral.500' }} />
                                                            </ListItemDecorator>
                                                            {getContentThumbnail(item) && (
                                                                <ListItemDecorator sx={{ mr: 2 }}>
                                                                    <Box
                                                                        component="img"
                                                                        src={getContentThumbnail(item)}
                                                                        alt={getPaneItemName(item)}
                                                                        sx={{
                                                                            width: 100,
                                                                            height: 100,
                                                                            objectFit: 'cover',
                                                                            borderRadius: '4px',
                                                                            border: '1px solid',
                                                                            borderColor: 'neutral.200'
                                                                        }}
                                                                    />
                                                                </ListItemDecorator>
                                                            )}
                                                            <ListItemContent>
                                                                <Typography level="body-sm" sx={{ fontWeight: 600, mb: 1 }}>
                                                                    {index + 1}. {getPaneItemName(item)}
                                                                </Typography>

                                                                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                                                    <Chip size="sm" color="primary" variant="soft">
                                                                        {item.duration}s
                                                                    </Chip>
                                                                    <Chip size="sm" color="neutral" variant="soft">
                                                                        {item.animation.replace('-', ' ')}
                                                                    </Chip>
                                                                </Stack>
                                                            </ListItemContent>
                                                            <ListItemDecorator>
                                                                <IconButton
                                                                    size="sm"
                                                                    variant="outlined"
                                                                    color="danger"
                                                                    aria-label={`Delete ${getPaneItemName(item)}`}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        deleteContentItem('rightPane', item.id);
                                                                    }}
                                                                >
                                                                    <Delete />
                                                                </IconButton>
                                                            </ListItemDecorator>
                                                        </ListItem>
                                                    ))}
                                                </List>

                                                <Button
                                                    variant="outlined"
                                                    startDecorator={<Add />}
                                                    onClick={() => openContentManagement('rightPane')}
                                                >
                                                    Add Content Item
                                                </Button>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>}

                                <Grid xs={12}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                <Typography level="h4" sx={{ fontWeight: 600 }}>
                                                    📰 Marquee Ticker
                                                </Typography>
                                                <Button
                                                    size="sm"
                                                    variant="outlined"
                                                    onClick={() => openPropertiesEditor('marquee')}
                                                >
                                                    ⚙️ Properties
                                                </Button>
                                            </Box>
                                            <FormControl>
                                                <FormLabel>Marquee Text</FormLabel>
                                                <Textarea
                                                    value={layoutForm.marqueeContent}
                                                    onChange={(e) => setLayoutForm(prev => ({ ...prev, marqueeContent: e.target.value }))}
                                                    placeholder="Enter scrolling text for the marquee..."
                                                    minRows={2}
                                                />
                                            </FormControl>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="solid"
                                    onClick={handleSaveLayout}
                                    loading={saving}
                                    disabled={saving || !layoutForm.name}
                                >
                                    {isEditing ? '💾 Update Layout' : '🎬 Create Layout'}
                                </Button>
                                <Button variant="outlined" onClick={() => setOpenLayoutModal(false)}>
                                    Cancel
                                </Button>
                            </Stack>
                        </Stack>
                    </ModalDialog>
                </Modal>

                {/* Pane Item Preview Modal */}
                <Modal open={Boolean(previewItem)} onClose={closeItemPreview}>
                    <ModalDialog sx={{ width: '90vw', maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
                        {previewItem && (
                            <Stack spacing={2}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                                    <Box>
                                        <Typography level="h3">{getPaneItemName(previewItem)}</Typography>
                                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                            <Chip size="sm" variant="soft" color="primary">
                                                {getPaneLabel(previewPane)}
                                            </Chip>
                                            <Chip size="sm" variant="soft" color="neutral">
                                                Item {previewIndex + 1} of {previewItems.length}
                                            </Chip>
                                        </Stack>
                                    </Box>
                                    <IconButton variant="plain" onClick={closeItemPreview} aria-label="Close item preview">
                                        <Cancel />
                                    </IconButton>
                                </Box>

                                <Box sx={{
                                    minHeight: 320,
                                    maxHeight: '50vh',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    backgroundColor: 'neutral.100',
                                    border: '1px solid',
                                    borderColor: 'neutral.200',
                                    borderRadius: 'md',
                                    p: 2
                                }}>
                                    {getContentPreviewUrl(previewItem) ? (
                                        <Box
                                            key={previewItem.id}
                                            component="img"
                                            src={getContentPreviewUrl(previewItem)}
                                            alt={getPaneItemName(previewItem)}
                                            onError={(e) => {
                                                const fallbackUrl = getContentPreviewFallbackUrl(previewItem);
                                                if (fallbackUrl && e.currentTarget.dataset.productionFallback !== 'true') {
                                                    e.currentTarget.dataset.productionFallback = 'true';
                                                    e.currentTarget.src = fallbackUrl;
                                                    return;
                                                }
                                                e.currentTarget.onerror = null;
                                                e.currentTarget.src = getFallbackThumbnail();
                                            }}
                                            sx={{
                                                display: 'block',
                                                width: '100%',
                                                height: '100%',
                                                maxHeight: '46vh',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    ) : (
                                        <Typography level="body-lg" sx={{ whiteSpace: 'pre-wrap', textAlign: 'center' }}>
                                            {previewItem.content}
                                        </Typography>
                                    )}
                                </Box>

                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1}
                                    sx={{ alignItems: 'center' }}
                                >
                                    <Stack direction="row" spacing={1} sx={{ flex: 1, justifyContent: 'center' }}>
                                        <Button
                                            variant="outlined"
                                            onClick={() => navigateItemPreview(-1)}
                                            disabled={previewIndex === 0}
                                        >
                                            ← Previous
                                        </Button>
                                        <Chip variant="soft" color="neutral">
                                            {previewIndex + 1} / {previewItems.length}
                                        </Chip>
                                        <Button
                                            variant="outlined"
                                            onClick={() => navigateItemPreview(1)}
                                            disabled={previewIndex === previewItems.length - 1}
                                        >
                                            Next →
                                        </Button>
                                    </Stack>
                                    <Button
                                        variant="soft"
                                        color="success"
                                        startDecorator={<Add />}
                                        onClick={() => openContentManagement(previewPane, previewIndex)}
                                        sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}
                                    >
                                        Add New
                                    </Button>
                                </Stack>

                                <Card variant="soft">
                                    <CardContent>
                                        <Typography level="title-md" sx={{ mb: 2 }}>Item settings</Typography>
                                        <Grid container spacing={2}>
                                            <Grid xs={12}>
                                                <FormControl>
                                                    <FormLabel>Source / image name</FormLabel>
                                                    <Input
                                                        value={previewForm.displayName}
                                                        onChange={(e) => setPreviewForm(prev => ({ ...prev, displayName: e.target.value }))}
                                                        placeholder="Enter a display name"
                                                    />
                                                    <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.tertiary' }}>
                                                        This name is used only in this layout; the shared content asset is unchanged.
                                                    </Typography>
                                                </FormControl>
                                            </Grid>
                                            <Grid xs={12} sm={4}>
                                                <FormControl>
                                                    <FormLabel>Position</FormLabel>
                                                    <Input
                                                        type="number"
                                                        value={previewForm.position}
                                                        onChange={(e) => setPreviewForm(prev => ({ ...prev, position: e.target.value }))}
                                                        slotProps={{
                                                            input: {
                                                                min: 1,
                                                                max: previewItems.length
                                                            }
                                                        }}
                                                    />
                                                </FormControl>
                                            </Grid>
                                            <Grid xs={12} sm={4}>
                                                <FormControl>
                                                    <FormLabel>Duration (seconds)</FormLabel>
                                                    <Input
                                                        type="number"
                                                        value={previewForm.duration}
                                                        onChange={(e) => setPreviewForm(prev => ({ ...prev, duration: e.target.value }))}
                                                        slotProps={{ input: { min: 1, max: 60 } }}
                                                    />
                                                </FormControl>
                                            </Grid>
                                            <Grid xs={12} sm={4}>
                                                <FormControl>
                                                    <FormLabel>Animation</FormLabel>
                                                    <Select
                                                        value={previewForm.animation}
                                                        onChange={(e, value) => setPreviewForm(prev => ({ ...prev, animation: value }))}
                                                    >
                                                        <Option value="fade">Fade In/Out</Option>
                                                        <Option value="slide-left">Slide Left</Option>
                                                        <Option value="slide-right">Slide Right</Option>
                                                        <Option value="flip-left">Flip Left</Option>
                                                        <Option value="flip-right">Flip Right</Option>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>

                                <Alert color="primary" variant="soft">
                                    Apply item changes here, then use “{isEditing ? 'Update Layout' : 'Create Layout'}” in the layout editor to save the layout.
                                </Alert>

                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1}
                                    sx={{ justifyContent: 'space-between' }}
                                >
                                    <Button
                                        variant="outlined"
                                        color="danger"
                                        startDecorator={<Delete />}
                                        onClick={deletePreviewItem}
                                    >
                                        Delete Item
                                    </Button>
                                    <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                                        <Button variant="solid" color="success" onClick={savePreviewChanges}>
                                            Apply Item Changes
                                        </Button>
                                        <Button variant="outlined" onClick={closeItemPreview}>
                                            Close
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Stack>
                        )}
                    </ModalDialog>
                </Modal>

                {/* Enhanced Content Selection Modal */}
                <Modal open={openContentModal} onClose={closeContentManagement}>
                    <ModalDialog sx={{
                        minWidth: '95vw',
                        maxWidth: '95vw',
                        minHeight: '90vh',
                        maxHeight: '90vh',
                        overflow: 'hidden',
                        p: 0
                    }}>
                        {/* Header */}
                        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'neutral.200' }}>
                            <Typography level="h4" sx={{ mb: 1 }}>
                                {editingContent
                                    ? '✏️ Edit Content Item'
                                    : contentInsertIndex !== null
                                        ? `➕ Insert Content before Item ${contentInsertIndex + 1} in ${getPaneLabel(currentPane)}`
                                        : `➕ Add Content to ${getPaneLabel(currentPane)}`}
                            </Typography>
                            <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                                Click folder to select all content (including subfolders) or select individual items
                            </Typography>
                        </Box>

                        {/* Main Content Area */}
                        <Box sx={{ display: 'flex', height: 'calc(90vh - 120px)', overflow: 'hidden' }}>
                            {/* Left Sidebar - Folder Tree */}
                            <Box sx={{
                                width: '300px',
                                borderRight: '1px solid',
                                borderColor: 'neutral.200',
                                backgroundColor: 'neutral.50',
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'neutral.200' }}>
                                    <Typography level="title-md" sx={{ mb: 1 }}>
                                        📁 Folders
                                    </Typography>
                                    <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
                                        Click folder to filter content
                                    </Typography>
                                </Box>

                                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                                    {/* All Content Button */}
                                    <Box
                                        sx={{
                                            p: 1,
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            backgroundColor: selectedFolderId === null ? 'primary.50' : 'transparent',
                                            border: selectedFolderId === null ? '1px solid' : '1px solid transparent',
                                            borderColor: selectedFolderId === null ? 'primary.200' : 'transparent',
                                            '&:hover': {
                                                backgroundColor: 'neutral.50'
                                            },
                                            mb: 2
                                        }}
                                        onClick={() => {
                                            setSelectedFolderId(null);
                                            setSelectedContentIds([]); // Clear individual selections when selecting "All Content"
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography>🏠</Typography>
                                            <Typography level="body-sm" sx={{ flex: 1 }}>
                                                All Content
                                            </Typography>
                                            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                                                {content.length}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Folder Tree using FolderTreeSelector */}
                                    <FolderTreeSelector
                                        folders={folders}
                                        selectedFolder={selectedFolderId}
                                        onSelect={(folderId) => {
                                            setSelectedFolderId(folderId);
                                            // Auto-select all content in folder when folder is selected
                                            const folderContent = getAllContentInFolderRecursive(folderId);
                                            setSelectedContentIds(folderContent.map(c => c.id));
                                        }}
                                        maxHeight="400px"
                                        showRootOption={false}
                                    />
                                </Box>
                            </Box>

                            {/* Right Side - Content Selection */}
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                {/* Content Grid - Scrollable */}
                                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                                    <Grid container spacing={2}>
                                        {getFilteredContent().map((item) => {
                                            const isSelected = selectedContentIds.includes(item.id);
                                            const thumbnailUrl = buildThumbnailUrl(item) || getFallbackThumbnail();
                                            const contentType = (item.type_name || '').toLowerCase();

                                            return (
                                                <Grid xs={6} sm={4} md={3} lg={2} key={item.id}>
                                                    <Card
                                                        variant={isSelected ? "solid" : "outlined"}
                                                        color={isSelected ? "primary" : "neutral"}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s',
                                                            '&:hover': {
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: 'sm'
                                                            }
                                                        }}
                                                        onClick={() => toggleContentSelection(item.id)}
                                                    >
                                                        <AspectRatio ratio="16/9">
                                                            <img
                                                                src={thumbnailUrl}
                                                                alt={item.content_name}
                                                                style={{ objectFit: 'cover' }}
                                                            />
                                                        </AspectRatio>
                                                        <CardContent sx={{ p: 1 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Checkbox
                                                                    checked={isSelected}
                                                                    onChange={() => toggleContentSelection(item.id)}
                                                                    onClick={(event) => event.stopPropagation()}
                                                                    aria-label={`Select ${item.content_name}`}
                                                                    size="sm"
                                                                />
                                                                <Typography level="body-sm" sx={{
                                                                    flex: 1,
                                                                    color: isSelected ? 'white' : 'inherit',
                                                                    fontWeight: isSelected ? 600 : 400
                                                                }} noWrap>
                                                                    {contentType === 'image' && '🖼️'}
                                                                    {contentType === 'video' && '🎥'}
                                                                    {contentType === 'pdf' && '📄'}
                                                                    {contentType === 'web' && '🌐'}
                                                                    {!['image', 'video', 'pdf', 'web'].includes(contentType) && '📝'}
                                                                    {' '}{item.content_name}
                                                                </Typography>
                                                            </Box>

                                                            {/* Folder Information */}
                                                            {item.folder_name && (
                                                                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <Box sx={{
                                                                        width: 8,
                                                                        height: 8,
                                                                        borderRadius: '50%',
                                                                        backgroundColor: item.folder_color || '#1976d2'
                                                                    }} />
                                                                    <Typography level="body-xs" sx={{
                                                                        color: isSelected ? 'rgba(255,255,255,0.8)' : 'neutral.500',
                                                                        fontSize: '10px'
                                                                    }} noWrap>
                                                                        📁 {item.folder_name}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>

                                    {getFilteredContent().length === 0 && (
                                        <Box sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography level="body-md" sx={{ color: 'neutral.500' }}>
                                                {selectedFolderId ? 'No content available in this folder' : 'No content available'}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        {/* Footer with Settings and Actions */}
                        <Box sx={{
                            p: 3,
                            borderTop: '1px solid',
                            borderColor: 'neutral.200',
                            backgroundColor: 'neutral.50'
                        }}>
                            <Grid container spacing={3} sx={{ alignItems: 'end' }}>
                                <Grid xs={12} md={2}>
                                    <FormControl>
                                        <FormLabel>Duration (seconds)</FormLabel>
                                        <Input
                                            type="number"
                                            value={contentForm.duration}
                                            onChange={(e) => setContentForm(prev => ({ ...prev, duration: e.target.value }))}
                                            min="1"
                                            max="60"
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid xs={12} md={2}>
                                    <FormControl>
                                        <FormLabel>Animation</FormLabel>
                                        <Select
                                            value={contentForm.animation}
                                            onChange={(e, value) => setContentForm(prev => ({ ...prev, animation: value }))}
                                        >
                                            <Option value="fade">Fade In/Out</Option>
                                            <Option value="slide-left">Slide Left</Option>
                                            <Option value="slide-right">Slide Right</Option>
                                            <Option value="flip-left">Flip Left</Option>
                                            <Option value="flip-right">Flip Right</Option>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid xs={12} md={3}>
                                    <FormControl>
                                        <FormLabel>Custom Text</FormLabel>
                                        <Input
                                            placeholder="Enter custom text..."
                                            value={contentForm.content.includes('<div class="text-content">') ?
                                                contentForm.content.replace(/<[^>]*>/g, '') : ''}
                                            onChange={(e) => setContentForm(prev => ({
                                                ...prev,
                                                content: e.target.value ? `<div class="text-content">${e.target.value}</div>` : ''
                                            }))}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid xs={12} md={5}>
                                    <Box sx={{
                                        display: 'flex',
                                        gap: 1,
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                        height: '100%',
                                        minHeight: '56px' // Match input height
                                    }}>
                                        {/* Selection Info */}
                                        {selectedContentIds.length > 0 && (
                                            <Typography level="body-sm" sx={{ color: 'neutral.600', mr: 2 }}>
                                                {selectedContentIds.length} item(s) selected
                                            </Typography>
                                        )}

                                        {/* Clear Selection Button */}
                                        {selectedContentIds.length > 0 && (
                                            <Button
                                                size="sm"
                                                variant="outlined"
                                                color="neutral"
                                                onClick={() => {
                                                    setSelectedContentIds([]);
                                                    setSelectedFolderId(null);
                                                }}
                                                sx={{ mr: 1 }}
                                            >
                                                🗑️ Clear Selection
                                            </Button>
                                        )}

                                        {/* Action Buttons */}
                                        <Button
                                            variant="solid"
                                            color="success"
                                            disabled={selectedContentIds.length === 0 && !contentForm.content}
                                            onClick={() => {
                                                if (selectedContentIds.length > 0) {
                                                    addMultipleContentItems(currentPane);
                                                } else if (contentForm.content) {
                                                    addSingleContentItem(currentPane);
                                                }
                                            }}
                                        >
                                            ➕ {contentInsertIndex !== null ? 'Insert' : 'Add'} {selectedContentIds.length > 0 ? `${selectedContentIds.length} Items` : 'Text'}
                                        </Button>
                                        <Button variant="outlined" onClick={closeContentManagement}>
                                            Cancel
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </ModalDialog>
                </Modal>

                {/* Layout Properties Modal */}
                <Modal open={openPropertiesModal} onClose={() => setOpenPropertiesModal(false)}>
                    <ModalDialog sx={{ minWidth: '600px', maxHeight: '90vh', overflow: 'auto' }}>
                        <Typography level="h4" sx={{ mb: 2 }}>
                            ⚙️ {currentPropertyPane === 'marquee' ? 'Marquee' : getPaneLabel(currentPropertyPane)} Properties
                        </Typography>

                        <Stack spacing={3}>
                            <Alert color="primary" variant="soft">
                                Customize the appearance and behavior of the {currentPropertyPane === 'marquee'
                                    ? 'marquee ticker'
                                    : getPaneLabel(currentPropertyPane).toLowerCase()}.
                            </Alert>

                            {currentPropertyPane === 'marquee' ? (
                                // Marquee Properties
                                <Grid container spacing={3}>
                                    <Grid xs={12} md={6}>
                                        <FormControl>
                                            <FormLabel>Background Color</FormLabel>
                                            <Input
                                                type="color"
                                                value={layoutProperties.marquee.backgroundColor}
                                                onChange={(e) => updateLayoutProperty('backgroundColor', e.target.value)}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        <FormControl>
                                            <FormLabel>Text Color</FormLabel>
                                            <Input
                                                type="color"
                                                value={layoutProperties.marquee.textColor}
                                                onChange={(e) => updateLayoutProperty('textColor', e.target.value)}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        <FormControl>
                                            <FormLabel>Font Size (px)</FormLabel>
                                            <Input
                                                type="number"
                                                value={layoutProperties.marquee.fontSize}
                                                onChange={(e) => updateLayoutProperty('fontSize', parseInt(e.target.value))}
                                                min="12"
                                                max="72"
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        <FormControl>
                                            <FormLabel>Font Weight</FormLabel>
                                            <Select
                                                value={layoutProperties.marquee.fontWeight}
                                                onChange={(e, value) => updateLayoutProperty('fontWeight', value)}
                                            >
                                                <Option value="normal">Normal</Option>
                                                <Option value="bold">Bold</Option>
                                                <Option value="lighter">Light</Option>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        <FormControl>
                                            <FormLabel>Animation Speed</FormLabel>
                                            <Box sx={{ px: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography level="body-sm" sx={{ color: 'neutral.600' }}>Slow</Typography>
                                                    <Typography level="body-sm" sx={{ color: 'neutral.600' }}>Fast</Typography>
                                                </Box>
                                                <input
                                                    type="range"
                                                    min="5"
                                                    max="120"
                                                    value={125 - layoutProperties.marquee.speed}
                                                    onChange={(e) => {
                                                        // Flip the logic: min=fast, max=slow
                                                        const rawValue = parseInt(e.target.value);
                                                        const flippedValue = 125 - rawValue; // 5 becomes 120, 120 becomes 5
                                                        updateLayoutProperty('speed', flippedValue);
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        height: '6px',
                                                        borderRadius: '3px',
                                                        background: '#e0e0e0',
                                                        outline: 'none',
                                                        appearance: 'none',
                                                        WebkitAppearance: 'none'
                                                    }}
                                                />
                                            </Box>
                                        </FormControl>
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        <FormControl>
                                            <FormLabel>Height (px)</FormLabel>
                                            <Input
                                                type="number"
                                                value={layoutProperties.marquee.height}
                                                onChange={(e) => updateLayoutProperty('height', parseInt(e.target.value))}
                                                min="40"
                                                max="120"
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        <FormControl>
                                            <FormLabel>Padding (px)</FormLabel>
                                            <Input
                                                type="number"
                                                value={layoutProperties.marquee.padding}
                                                onChange={(e) => updateLayoutProperty('padding', parseInt(e.target.value))}
                                                min="5"
                                                max="30"
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        <FormControl>
                                            <FormLabel>Top Border</FormLabel>
                                            <Input
                                                value={layoutProperties.marquee.borderTop}
                                                onChange={(e) => updateLayoutProperty('borderTop', e.target.value)}
                                                placeholder="3px solid #1976d2"
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        <FormControl>
                                            <FormLabel>Bottom Border</FormLabel>
                                            <Input
                                                value={layoutProperties.marquee.borderBottom}
                                                onChange={(e) => updateLayoutProperty('borderBottom', e.target.value)}
                                                placeholder="3px solid #1976d2"
                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            ) : (
                                // Pane Properties (Left/Right)
                                <Grid container spacing={3}>
                                    <Grid xs={12} md={6}>
                                        <FormControl>
                                            <FormLabel>Background Color</FormLabel>
                                            <Input
                                                type="color"
                                                value={layoutProperties[currentPropertyPane].backgroundColor}
                                                onChange={(e) => updateLayoutProperty('backgroundColor', e.target.value)}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        <FormControl>
                                            <FormLabel>Text Color</FormLabel>
                                            <Input
                                                type="color"
                                                value={layoutProperties[currentPropertyPane].textColor}
                                                onChange={(e) => updateLayoutProperty('textColor', e.target.value)}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        <FormControl>
                                            <FormLabel>Padding (px)</FormLabel>
                                            <Input
                                                type="number"
                                                value={layoutProperties[currentPropertyPane].padding}
                                                onChange={(e) => updateLayoutProperty('padding', parseInt(e.target.value))}
                                                min="5"
                                                max="50"
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        <FormControl>
                                            <FormLabel>Border Radius (px)</FormLabel>
                                            <Input
                                                type="number"
                                                value={layoutProperties[currentPropertyPane].borderRadius}
                                                onChange={(e) => updateLayoutProperty('borderRadius', parseInt(e.target.value))}
                                                min="0"
                                                max="20"
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        <FormControl>
                                            <FormLabel>Border Width (px)</FormLabel>
                                            <Input
                                                type="number"
                                                value={layoutProperties[currentPropertyPane].borderWidth}
                                                onChange={(e) => updateLayoutProperty('borderWidth', parseInt(e.target.value))}
                                                min="0"
                                                max="10"
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        <FormControl>
                                            <FormLabel>Border Color</FormLabel>
                                            <Input
                                                type="color"
                                                value={layoutProperties[currentPropertyPane].borderColor}
                                                onChange={(e) => updateLayoutProperty('borderColor', e.target.value)}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        <FormControl>
                                            <FormLabel>Font Size (px)</FormLabel>
                                            <Input
                                                type="number"
                                                value={layoutProperties[currentPropertyPane].fontSize}
                                                onChange={(e) => updateLayoutProperty('fontSize', parseInt(e.target.value))}
                                                min="12"
                                                max="48"
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        <FormControl>
                                            <FormLabel>Font Weight</FormLabel>
                                            <Select
                                                value={layoutProperties[currentPropertyPane].fontWeight}
                                                onChange={(e, value) => updateLayoutProperty('fontWeight', value)}
                                            >
                                                <Option value="normal">Normal</Option>
                                                <Option value="bold">Bold</Option>
                                                <Option value="lighter">Light</Option>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid xs={12}>
                                        <FormControl>
                                            <FormLabel>Text Alignment</FormLabel>
                                            <Select
                                                value={layoutProperties[currentPropertyPane].textAlign}
                                                onChange={(e, value) => updateLayoutProperty('textAlign', value)}
                                            >
                                                <Option value="left">Left</Option>
                                                <Option value="center">Center</Option>
                                                <Option value="right">Right</Option>
                                                <Option value="justify">Justify</Option>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            )}

                            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                                <Button
                                    variant="solid"
                                    onClick={() => setOpenPropertiesModal(false)}
                                >
                                    ✅ Apply Changes
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => resetLayoutProperties(currentPropertyPane)}
                                >
                                    🔄 Reset to Default
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => setOpenPropertiesModal(false)}
                                >
                                    Cancel
                                </Button>
                            </Stack>
                        </Stack>
                    </ModalDialog>
                </Modal>
            </Box>
        </MainLayout>
    );
};

export default LayoutsPage;
